/**
 * pdf.js, loaded once per session and early.
 *
 * Its code (about 1.8 MB before compression, most of it the worker) used to be
 * fetched only when the first PDF reached the stage, so the first presentation
 * of a session waited for it on top of the file itself. Opening Document share,
 * or a PDF arriving in the room's file list, now starts it in the background.
 *
 * One worker is kept for the session and handed to every document. pdf.js
 * leaves a worker it was given running when a document is destroyed, so the
 * next presentation does not start another.
 */
import type { PDFWorker } from "pdfjs-dist";

type PdfJs = typeof import("pdfjs-dist/legacy/build/pdf.mjs");
type PdfViewer = { pdfjs: PdfJs; worker: PDFWorker | null };

const WORKER_URL = new URL("pdfjs-dist/legacy/build/pdf.worker.min.mjs", import.meta.url).toString();

let loading: Promise<PdfViewer> | null = null;

/** pdf.js and the session's worker. The worker is null if it would not start. */
export function loadPdfViewer(): Promise<PdfViewer> {
  if (loading) return loading;
  loading = (async () => {
    // The legacy build: the modern one needs Promise.withResolvers, which
    // iOS Safari only gained in 17.4.
    const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
    pdfjs.GlobalWorkerOptions.workerSrc = WORKER_URL;
    let worker: PDFWorker | null = null;
    try {
      worker = new pdfjs.PDFWorker();
      await worker.promise;
    } catch {
      // Each document then starts its own, as before.
      worker = null;
    }
    return { pdfjs, worker };
  })();
  // A failed download (a dropped connection) is tried again next time.
  loading.catch(() => {
    loading = null;
  });
  return loading;
}

/** Start loading it now, for a PDF that may soon be presented. */
export function preloadPdfViewer(): void {
  void loadPdfViewer().catch(() => {});
}
